<template>
  <div class="gallery-block">
    <div v-if="showFilters" :class="{ 'filters-preview': clickAction === 'none' }">
    <GalleryFilters
      :tag-groups="artistTagGroups"
      :selected="filterTags"
      :search="filterSearch"
      :availability="filterAvailability"
      :size="filterSize"
      :size-options="sizeOptions"
      :size-range="filterSizeRange"
      :size-unit-label="sizeUnitLabel"
      :count="filteredImages.length"
      :total-count="totalVisibleWorks"
      :fully-loaded="worksFullyLoaded"
      :open="filtersOpen"
      @update:search="filterSearch = $event"
      @update:selected="filterTags = $event"
      @update:availability="filterAvailability = $event"
      @update:size="filterSize = $event"
      @update:size-range="filterSizeRange = $event"
      @update:open="filtersOpen = $event"
      @clear="clearFilters"
    />
    </div>
    <GalleryGrid
      v-if="filteredImages.length > 0"
      :images="displayImages"
      :layout="layout"
      :columns="columns"
      :gap="gap"
      :aspect-ratio="gridAspect"
      :row-height="rowHeight"
      :show-caption="showCaptions"
      :overlay-style="overlayStyle"
      :show-overlay-subtext="showOverlaySubtext"
      :overlay-subtext="overlaySubtextValue"
      :show-sold-badge="showSoldBadge"
      :show-price-in-overlay="showPriceInOverlay"
      :show-price-when-sold="showPriceWhenSold"
      :title-font-size="titleFontSize"
      :hover-effect="hoverEffect"
      :image-style="imageStyle"
      :click-action="clickAction"
      :pin-hover="pinHover"
      @item-click="handleItemClick"
    />

    <div v-else-if="worksReady && isFiltered" class="filter-empty visitor-theme">
      <p>No works match your filters.</p>
      <button class="clear-filters-link" title="Clear filters" aria-label="Clear filters" @click="clearFilters">Clear filters</button>
    </div>
    <div v-else-if="worksReady && preview" class="block-empty">
      <p>No images in this gallery</p>
    </div>

    <!-- Infinite scroll sentinel -->
    <div v-if="isInfiniteScroll" ref="sentinel" class="scroll-sentinel" />

    <!-- Pagination (only in numbered/classical mode) -->
    <GalleryPagination
      v-if="!isInfiniteScroll"
      :current-page="currentPage"
      :total-pages="noOfPages"
      @page-change="setCurrentPage"
    />
  </div>
</template>

<script>
const INFINITE_SCROLL_BATCH = 24

import { store, mutations } from '@/store'
import { artistConfig as defaultConfig } from '@/config/artist.config'
import { normalizeTagGroups, workMatchesTags, hasActiveTagFilters } from '@/utils/tags'
import { startClickToModal, markClickToModal } from '@/utils/perfMarks'
import { bestThumbUrl } from '@/utils/imageUrl'
import { syncedGalleryImages, customGalleryImages } from '@/utils/galleryImages'
import { visitorUnit, unitLabel, sizeBracket, sizeBracketOptions, EMPTY_SIZE_RANGE, hasSizeRange, matchesSizeRange } from '@/utils/sizeUnits'
import GalleryGrid from '@/components/gallery/GalleryGrid.vue'
import GalleryPagination from '@/components/GalleryPagination.vue'
import GalleryFilters from '@/components/gallery/GalleryFilters.vue'

export default {
  name: 'GalleryBlock',
  components: {
    GalleryGrid,
    GalleryPagination,
    GalleryFilters
  },
  props: {
    // Block data object (used in custom pages). Optional if images prop is provided.
    data: {
      type: Object,
      default: () => ({})
    },
    // Optional: external page control (1-indexed). When provided, pagination is controlled externally
    page: {
      type: Number,
      default: null
    },
    // Optional: provide images directly instead of via data.images
    images: {
      type: Array,
      default: null
    },
    // Override click behaviour (passed to GalleryGrid). Use 'none' for preview contexts.
    clickAction: {
      type: String,
      default: 'emit'
    },
    // Show the first thumbnail as if hovered (settings preview)
    pinHover: {
      type: Boolean,
      default: false
    },
    // Editor preview: show the empty note; visitors see nothing
    preview: {
      type: Boolean,
      default: false
    }
  },
  emits: ['page-change', 'item-click'],
  data() {
    return {
      internalPage: 1,
      filterSearch: '',
      filterTags: {},
      filterAvailability: '',
      filterSize: '',
      filterSizeRange: { ...EMPTY_SIZE_RANGE },
      filtersOpen: false,
      renderedCount: 0,
      scrollObserver: null
    }
  },
  computed: {
    worksReady() {
      return store.worksReady
    },
    worksFullyLoaded() {
      return store.worksFullyLoaded
    },
    totalVisibleWorks() {
      // The fast server count is site-wide - only valid for an unfiltered synced
      // gallery. Tag-scoped and manual galleries fall back to the resolved list.
      if (this.isSyncAllWorks && !this.data.syncTagIds?.length) return store.totalVisibleWorks
      return this.sortedImages.length
    },
    lastClosedWork() {
      return store.lastClosedWork
    },
    // Global gallery config from store (with defaults)
    globalGalleryConfig() {
      const config = store.artistConfig || defaultConfig
      return config.gallery || {}
    },
    // All works from store (for looking up by workId)
    allWorks() {
      return store.allWorks || []
    },
    // When syncAllWorks is enabled, use all visible works from store
    isSyncAllWorks() {
      return this.data.syncAllWorks === true
    },
    // Use images prop if provided, otherwise fall back to data.images
    // When syncAllWorks is true, use all visible works from store
    // Supports both formats:
    // - Full data: { workId, slug, imageUrl, thumbnailUrl, ... } (legacy)
    // - Reference only: { workId, order } (normalized - looks up from store)
    // Derivation shared with computePreloadImages via utils/galleryImages.js.
    sortedImages() {
      if (this.isSyncAllWorks && !this.images) {
        return syncedGalleryImages(this.allWorks, this.data.syncTagIds)
      }
      return customGalleryImages(this.images || this.data.images || [], this.allWorks, {
        includeArchived: this.data.includeArchived === true
      })
    },
    // Current page: use external page prop if provided, otherwise internal state
    currentPage() {
      return this.page !== null ? this.page : this.internalPage
    },
    activeFilters() {
      return {
        search: this.filterSearch,
        tags: this.filterTags,
        availability: this.filterAvailability,
        size: this.filterSize,
        sizeRange: this.filterSizeRange
      }
    },
    // Pagination mode from global config
    paginationMode() {
      return this.globalGalleryConfig.pagination || 'classical'
    },
    isInfiniteScroll() {
      return this.paginationMode === 'infinite'
    },
    itemsPerPage() {
      return this.globalGalleryConfig.itemsPerPage ?? 12
    },
    layout() {
      return this.globalGalleryConfig.layout ?? 'rows'
    },
    columns() {
      return this.globalGalleryConfig.columns ?? 3
    },
    gap() {
      return this.globalGalleryConfig.gap ?? 15
    },
    gridAspect() {
      return this.globalGalleryConfig.gridAspect ?? 'square'
    },
    rowHeight() {
      return this.globalGalleryConfig.rowHeight ?? 200
    },
    showCaptions() {
      return this.globalGalleryConfig.showCaptions ?? false
    },
    // Visual styling settings from global config
    overlayStyle() {
      return this.globalGalleryConfig.overlayStyle ?? 'full'
    },
    showOverlaySubtext() {
      return this.globalGalleryConfig.showOverlaySubtext ?? true
    },
    overlaySubtextValue() {
      return this.globalGalleryConfig.overlaySubtextValue || 'Read more'
    },
    showSoldBadge() {
      return this.globalGalleryConfig.showSoldBadge ?? true
    },
    showPriceInOverlay() {
      return this.globalGalleryConfig.showPriceInOverlay ?? false
    },
    // Same rule as the opened artwork: a sold work's price shows only when allowed
    showPriceWhenSold() {
      return this.globalGalleryConfig.viewerShowPriceWhenSold === true
    },
    titleFontSize() {
      return this.globalGalleryConfig.titleFontSize ?? 18
    },
    hoverEffect() {
      return this.globalGalleryConfig.hoverEffect ?? 'none'
    },
    imageStyle() {
      return this.globalGalleryConfig.imageStyle ?? 'none'
    },
    // Narrowed to the gallery's own sync tags when set, per group independently:
    // a group untouched by the sync filter (no tags from it selected) still
    // offers every tag in it; a group narrowed to one tag is dropped (every
    // work already has it, so the option would filter nothing); two or more
    // narrows the group's options down to just the selected tags.
    artistTagGroups() {
      const groups = normalizeTagGroups((store.artistConfig || defaultConfig).artist?.tagGroups)
      const syncTagIds = this.data.syncTagIds
      if (!this.isSyncAllWorks || !Array.isArray(syncTagIds) || !syncTagIds.length) return groups
      return groups
        .map(group => {
          const selected = group.tags.filter(tag => syncTagIds.includes(tag.id))
          if (!selected.length) return group
          if (selected.length === 1) return { ...group, tags: [] }
          return { ...group, tags: selected }
        })
        .filter(group => group.tags.length)
    },
    sizeUnit() {
      return visitorUnit()
    },
    sizeUnitLabel() {
      return unitLabel(this.sizeUnit)
    },
    // Each work's bracket, worked out once per works list
    bracketByWork() {
      return new Map(this.sortedImages.map(w => [w, sizeBracket(w.size, this.sizeUnit)]))
    },
    // The brackets this gallery's works fall into; none (size filter hidden) without any sized work
    sizeOptions() {
      const present = new Set(this.bracketByWork.values())
      return sizeBracketOptions(this.sizeUnit).filter(o => present.has(o.value))
    },
    showFilters() {
      return this.globalGalleryConfig.showFilterBar !== false
    },
    isFiltered() {
      return !!(this.filterSearch || hasActiveTagFilters(this.filterTags) || this.filterAvailability || this.filterSize || hasSizeRange(this.filterSizeRange))
    },
    filteredImages() {
      let works = this.sortedImages
      if (this.filterSearch) {
        const q = this.filterSearch.toLowerCase()
        works = works.filter(w => (w.name || '').toLowerCase().includes(q))
      }
      if (hasActiveTagFilters(this.filterTags)) {
        works = works.filter(w => workMatchesTags(w, this.filterTags))
      }
      if (this.filterAvailability === 'available') {
        works = works.filter(w => !w.isSold)
      } else if (this.filterAvailability === 'sold') {
        works = works.filter(w => w.isSold)
      }
      if (this.filterSize) {
        works = works.filter(w => this.bracketByWork.get(w) === this.filterSize)
      }
      if (hasSizeRange(this.filterSizeRange)) {
        works = works.filter(w => matchesSizeRange(w.size, this.filterSizeRange, this.sizeUnit))
      }
      return works
    },
    // Pagination
    noOfPages() {
      if (this.isInfiniteScroll) return 1
      const total = (!this.worksFullyLoaded && this.totalVisibleWorks !== null)
        ? this.totalVisibleWorks
        : this.filteredImages.length
      return Math.ceil(total / this.itemsPerPage)
    },
    displayImages() {
      if (this.isInfiniteScroll) {
        return this.filteredImages.slice(0, this.renderedCount)
      }
      const startIndex = (this.currentPage - 1) * this.itemsPerPage
      return this.filteredImages.slice(startIndex, startIndex + this.itemsPerPage)
    },
    // Determine the current page slug for building work routes
    currentPageSlug() {
      const routeName = this.$route.name
      if (routeName === 'Home') return null
      return this.$route.params.slug || null
    }
  },
  watch: {
    worksReady(val) {
      if (val && this.isInfiniteScroll) this.setupInfiniteScroll()
    },
    isInfiniteScroll(val) {
      if (val && this.worksReady) this.setupInfiniteScroll()
      else if (!val) this.teardownInfiniteScroll()
    },
    lastClosedWork(slug) {
      if (slug) {
        this.scrollToWork(slug)
        mutations.clearLastClosedWork()
      }
    },
    activeFilters(val) {
      this.internalPage = 1
      mutations.setGalleryFilters({ ...val, routePath: this.$route?.path })
      if (this.isInfiniteScroll) this.resetInfiniteScroll()
    },
    filtersOpen(val) { mutations.setGalleryFilters({ filtersOpen: val, routePath: this.$route?.path }) },
    noOfPages(val) {
      if (val > 0 && this.internalPage > val) {
        this.internalPage = val
        this.$router.replace({ query: { ...this.$route.query, page: val } })
      }
    },
    currentPage: {
      handler() { this.prefetchNextPage() },
      immediate: true
    },
    filteredImages() {
      this.prefetchNextPage()
    }
  },
  created() {
    const stored = store.galleryFilters
    if (stored.routePath !== null && stored.routePath === this.$route?.path) {
      this.filterSearch = stored.search
      this.filterTags = stored.tags ? { ...stored.tags } : {}
      this.filterAvailability = stored.availability
      this.filterSize = stored.size || ''
      this.filterSizeRange = { ...EMPTY_SIZE_RANGE, ...stored.sizeRange }
      this.filtersOpen = stored.filtersOpen
    }
    const queryPage = parseInt(this.$route?.query?.page)
    if (!isNaN(queryPage) && queryPage > 0) {
      this.internalPage = queryPage
    }
  },
  mounted() {
    if (this.worksReady && this.isInfiniteScroll) this.setupInfiniteScroll()
  },
  beforeUnmount() {
    this.teardownInfiniteScroll()
  },

  methods: {
    setupInfiniteScroll(resetCount = true) {
      this.teardownInfiniteScroll()
      if (resetCount) this.renderedCount = INFINITE_SCROLL_BATCH
      this.$nextTick(() => {
        const sentinel = this.$refs.sentinel
        if (!sentinel) return
        this.scrollObserver = new IntersectionObserver((entries) => {
          if (entries[0].isIntersecting) this.loadMoreItems()
        }, { rootMargin: '400px' })
        this.scrollObserver.observe(sentinel)
      })
    },
    teardownInfiniteScroll() {
      if (this.scrollObserver) {
        this.scrollObserver.disconnect()
        this.scrollObserver = null
      }
    },
    resetInfiniteScroll() {
      this.renderedCount = INFINITE_SCROLL_BATCH
    },
    loadMoreItems() {
      if (this.renderedCount >= this.filteredImages.length) return
      this.renderedCount = Math.min(this.renderedCount + INFINITE_SCROLL_BATCH, this.filteredImages.length)
      if (this.renderedCount >= this.filteredImages.length) {
        this.teardownInfiniteScroll()
      }
    },
    scrollToWork(slug) {
      if (!slug) return
      const idx = this.filteredImages.findIndex(w => w.slug === slug || w.id === slug)
      if (idx === -1) return
      if (this.isInfiniteScroll) {
        if (idx >= this.renderedCount) this.renderedCount = idx + 1
      } else {
        const targetPage = Math.floor(idx / this.itemsPerPage) + 1
        if (targetPage !== this.currentPage) this.internalPage = targetPage
      }
      this.$nextTick(() => {
        const el = this.$el.querySelector(`[data-work-slug="${slug}"]`)
        if (el) el.scrollIntoView({ behavior: 'instant', block: 'center' })
      })
    },
    // Warms the browser's HTTP cache for the next page's thumbnails while the
    // visitor is still looking at the current page, so clicking Next doesn't
    // start those downloads cold. Infinite scroll already has its own lookahead
    // via the IntersectionObserver rootMargin, so this only applies to classical pagination.
    prefetchNextPage() {
      if (this.isInfiniteScroll) return
      const start = this.currentPage * this.itemsPerPage
      const nextPageImages = this.filteredImages.slice(start, start + this.itemsPerPage)
      if (!nextPageImages.length) return
      const schedule = typeof window.requestIdleCallback === 'function'
        ? window.requestIdleCallback
        : (cb) => setTimeout(cb, 200)
      schedule(() => {
        nextPageImages.forEach(image => {
          const url = bestThumbUrl(image)
          if (url) new Image().src = url
        })
      })
    },
    clearFilters() {
      this.filterSearch = ''
      this.filterTags = {}
      this.filterAvailability = ''
      this.filterSize = ''
      this.filterSizeRange = { ...EMPTY_SIZE_RANGE }
    },
    setCurrentPage(page) {
      this.$emit('page-change', page)
      if (this.page === null) {
        this.internalPage = page
        this.$router.replace({ query: { ...this.$route.query, page } })
      }
      this.$el.scrollIntoView({ behavior: 'instant', block: 'start' })
    },
    handleItemClick(image) {
      startClickToModal()
      markClickToModal('item:click')
      // Emit event for parent to handle
      this.$emit('item-click', image)
      // Default behavior: navigate to work modal
      if (image.slug) {
        const pageSlug = this.currentPageSlug
        // Set navigation context so WorkModal only navigates within this gallery's works
        mutations.setNavigationContext({
          works: this.filteredImages,
          sourcePath: this.$route.fullPath,
          pageSlug
        })
        markClickToModal('router:push')
        this.$router.push({
          name: 'work',
          params: { workSlug: image.slug, ...(pageSlug && { pageSlug }) }
        })
      }
    }
  }
}
</script>

<style scoped>
.gallery-block {
  width: 100%;
}

.filters-preview {
  pointer-events: none;
  user-select: none;
}

.filter-empty {
  text-align: center;
  padding: 40px 20px;
  color: var(--color-text);

  p {
    margin: 0 0 0.75rem;
    font-size: 0.9rem;
    font-style: italic;
  }
}

.clear-filters-link {
  padding: 0;
  border: none;
  background: transparent;
  color: var(--color-highlight);
  font: inherit;
  font-size: 0.9rem;
  cursor: pointer;
  text-decoration: underline;
  text-underline-offset: 2px;

  &:hover {
    color: var(--color-text);
  }
}

.scroll-sentinel {
  height: 1px;
}
</style>
